import { Finding } from '@telespot/domain';

import { Roi } from '../../entities/roi.model';
import { MosaicMapper } from '../mosaic-mapper';

export class ReviewServiceError {
  public static DESTINATION_FINDING_NOT_FOUND = 'Destination finding not found';
}

export interface CopyRoisResult {
  /**
   * Copy of source rois for the reviewing analyst
   */
  copiedRois: Roi[];
  /**
   * Ids of the reviewer findings that were changed by the review
   * Used to determine which findings should be deleted/emptied
   */
  changedFindings: Set<string>;
}

const roiMatcher = (sourceRoi: Roi, destinationRois: Roi[]) =>
  destinationRois.find(
    (destinationRoi) =>
      destinationRoi.assetId === sourceRoi.assetId && destinationRoi.pipelineStepId === sourceRoi.pipelineStepId
  );

export class ReviewService {
  /**
   * Copies the source rois for the reviewing analyst
   * @param sourceRois - The source rois to copy
   * @param analystId - Ids of the analyst performing the review
   * @param existingRois - The existing rois to copy
   * @returns A {@link CopyRoisResult} object
   */
  public static copyRois(sourceRois: Roi[], analystId: string, existingRois: Roi[]): CopyRoisResult {
    const copiedRois: Roi[] = [];

    const roiCache: Record<string, Roi> = {};
    const changedFindings: Set<string> = new Set();

    for (const sourceRoi of sourceRois) {
      const findingKey = [sourceRoi.assetId, sourceRoi.pipelineStepId].join('_');
      const destinationRoi = (roiCache[findingKey] ??= roiMatcher(sourceRoi, existingRois));

      const newRoiId = MosaicMapper.createSpatialKey({
        ...sourceRoi.coordinates,
        findingId: destinationRoi?.findingId,
      });

      const newRoi: Roi = {
        ...sourceRoi,
        id: newRoiId,
        findingId: destinationRoi?.findingId,
        findingVersion: destinationRoi?.findingVersion,
        findingVersionId: destinationRoi?.findingVersionId,
        creatorId: destinationRoi?.creatorId ?? analystId,
        creatorEntity: destinationRoi?.creatorEntity ?? 'user',
        analysisId: destinationRoi?.analysisId,
        originFindingVersionId: sourceRoi.findingVersionId,
      };

      copiedRois.push(newRoi);

      if (destinationRoi) changedFindings.add(destinationRoi.findingId);
    }

    return { copiedRois, changedFindings };
  }

  /**
   * Creates new emptry versions for the specified findings
   * @param changedFindings - Ids of the reviewer findings that were changed by the review
   * @param existingFindings - The existing findings to create empty findings from
   * @returns Array of empty findings
   */
  public static createEmptyFindings(existingFindings: Finding[]): Finding[] {
    return existingFindings.map(
      (finding) =>
        new Finding({
          creatorId: finding.creatorId,
          creatorEntity: finding.creatorEntity,
          analysisId: finding.analysisId,
          pipelineStepId: finding.pipelineStepId,
          data: { content: [] },
          type: finding.type,
          version: undefined,
          originId: undefined,
          pipelineId: finding.pipelineId,
          assetId: finding.assetId,
          uuid: finding.uuid,
          id: undefined,
        })
    );
  }
}
